import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, KeyRound, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'odelicieuse_email_nudge_until';

// Invite les clients connectés sans e-mail à en ajouter un (récupération de
// compte) et rappelle de changer un mot de passe temporaire.
const AccountNudge = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => { setHidden(false); }, [user?.id]);

  if (!user || user.role !== 'customer' || hidden) return null;
  if (location.pathname.startsWith('/connexion') || location.pathname.startsWith('/inscription')) return null;

  const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
  const needsEmail = !user.email && Date.now() > dismissedUntil;
  const mustChange = !!user.must_change_password && !location.pathname.startsWith('/compte');

  if (!needsEmail && !mustChange) return null;

  const later = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 86400 * 1000));
    setHidden(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/email`, { email });
      toast.success(t('nudge_email_saved'));
      await refreshUser?.();
      setHidden(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : t('nudge_email_error'));
    } finally {
      setSaving(false);
    }
  };

  if (mustChange) {
    return (
      <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:w-96 z-50 bg-[color:var(--color-surface)] border border-[color:var(--color-primary)] p-4 shadow-2xl" data-testid="must-change-password-banner">
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-white text-sm">{t('nudge_temp_password')}</p>
            <Link to="/compte" className="inline-block mt-2 text-[color:var(--color-primary)] text-sm underline underline-offset-4" data-testid="must-change-password-link">{t('nudge_change_now')}</Link>
          </div>
          <button type="button" onClick={() => setHidden(true)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="email-nudge-modal">
      <form onSubmit={save} className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.40)] p-6 w-full max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <Mail className="w-6 h-6 text-[color:var(--color-primary)]" />
          <h3 className="text-white text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('nudge_email_title')}</h3>
        </div>
        <p className="text-zinc-400 text-sm mb-4">{t('nudge_email_desc')}</p>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@exemple.com"
          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none mb-4"
          data-testid="email-nudge-input" />
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={later} className="px-4 py-2 text-zinc-400 hover:text-white text-sm" data-testid="email-nudge-later">{t('nudge_later')}</button>
          <button type="submit" disabled={saving} className="px-5 py-2 bg-[color:var(--color-primary)] text-black text-sm hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50" data-testid="email-nudge-save">
            {saving ? '...' : t('nudge_save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountNudge;
